import { createDeferred } from '@forest/native-services'
import {
  createEffect,
  createSignal,
  onCleanup,
  untrack,
  type Accessor,
} from 'solid-js'
import { logNonFatal } from '~/lib/diagnostics'
import { mark } from '~/lib/perf'
import { REVEAL_DEADLINE_MS, type RevealReason } from '~/lib/reveal'
import {
  classicSurfaceProgress,
  type CatalogueGridSlot,
} from './catalogue-grid'

export interface ClassicReveal {
  // Starts the Classic reveal; later calls no-op. The shell calls it
  // whenever the Classic tab is showing.
  enter: () => void
  // Resolves on the first entry into the Classic tab: the Classic surface's
  // slots start (the gate's opened).
  entered: Promise<void>
  // Resolves at the Classic reveal: the rest of the grid starts.
  released: Promise<void>
  // Reactive: true once the catalogue skeleton has been asked to give way.
  revealed: Accessor<boolean>
}

// The Classic reveal (GLOSSARY.md; ADR 0049), for a page load that did not
// land on Classic: the first entry into the Classic tab hydrates the Classic
// landing's reveal surface behind a catalogue-only skeleton, and whichever
// comes first — that surface settled, or the deadline counted from the
// entry — swaps the skeleton for the cards and releases the rest of the
// grid. Leaving the tab does not stop it.
export function createClassicReveal(
  slots: Accessor<CatalogueGridSlot[] | undefined>,
): ClassicReveal {
  const [hasEntered, setHasEntered] = createSignal(false)
  const [revealed, setRevealed] = createSignal(false)
  const entered = createDeferred()
  const released = createDeferred()

  const reveal = (reason: RevealReason) => {
    if (untrack(revealed)) return false
    mark('classic-reveal', { reason })
    setRevealed(true)
    released.done()
    return true
  }

  createEffect(() => {
    const current = slots()
    if (!current || !hasEntered()) return
    const { settled, size } = classicSurfaceProgress(current)
    if (settled === size) reveal('landing-settled')
  })

  createEffect(() => {
    if (!hasEntered() || untrack(revealed)) return
    const deadline = setTimeout(() => {
      if (!reveal('deadline')) return
      const { settled, size } = classicSurfaceProgress(untrack(slots) ?? [])
      logNonFatal('shop_classic_reveal_deadline_hit', {
        settled_window: `${settled}/${size}`,
      })
    }, REVEAL_DEADLINE_MS)
    onCleanup(() => clearTimeout(deadline))
  })

  return {
    enter: () => {
      if (untrack(hasEntered)) return
      mark('classic-entered')
      setHasEntered(true)
      entered.done()
    },
    entered: entered.promise,
    released: released.promise,
    revealed,
  }
}
